import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

// VINEY (T672 rule 3, session 3): the cart freshness gate, shared through two window numbers with
// product-form.js, cart-icon.js and vn-motion.js. Every request that reads or changes the cart is numbered
// when sent (__vnCartRead); its answer is applied only if no newer cart has been shown (__vnCartFresh),
// and applying it moves the line to its number. So no slow answer can paint an older cart over a newer one.
const vnCart = {
  next: () => {
    const w = /** @type {any} */ (window);
    return (w.__vnCartRead = (w.__vnCartRead || 0) + 1);
  },
  /** @param {number} n */
  stale: (n) => n < (/** @type {any} */ (window).__vnCartFresh || 0),
  /** @param {number} n */
  seen: (n) => {
    const w = /** @type {any} */ (window);
    w.__vnCartFresh = Math.max(w.__vnCartFresh || 0, n);
  },
  // Send order is not always the order the store applied things in, so an answer set aside as stale is
  // followed by one reconciliation (debounced, shared by every file through window): a fresh numbered read
  // of the cart and of every cart section, sent after the answer arrived, so the last thing shown is the
  // store's cart as it settled. Listeners: component-cart-items.js, cart-icon.js, vn-motion.js (the vine).
  reconcile: () => {
    const w = /** @type {any} */ (window);
    clearTimeout(w.__vnReconcileTimer);
    w.__vnReconcileTimer = setTimeout(() => {
      const e = /** @type {any} */ (new CustomEvent('vn:cart-reconcile'));
      e.vnGen = vnCart.next();
      document.dispatchEvent(e);
    }, 300);
  },
};

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    // VINEY (T679 rule 9, R2-6Z/R2-9Z): codes are typed in capitals; spaces around are dropped
    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) {
      discountCode.focus();
      return;
    }

    // VINEY: "Nota" shows its wait line while the cart answers; a second press while it waits does
    // nothing (it would abort the first request, whose cleanup then freed the second one's button)
    const applyButton = form.querySelector('button[type="submit"]');
    if (applyButton?.hasAttribute('data-vn-wait')) return;
    const abortController = this.#createAbortController();
    // the field loses an earlier wrong mark
    applyButton?.setAttribute('data-vn-wait', '');
    discountCode.closest('.vn-fw')?.classList.remove('is-bad');

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) return;

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      // VINEY: numbered when sent (the freshness gate above)
      const vnRead = vnCart.next();
      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();
      Object.defineProperty(data, '__vnRead', { value: vnRead });

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        // VINEY (live bug 4): a refused code stays in the field, marked wrong, so it can be fixed
        this.#handleDiscountError('discount_code');
        discountCode.closest('.vn-fw')?.classList.add('is-bad');
        discountCode.setAttribute('aria-invalid', 'true');
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          // VINEY (live bug 4): the code stays in the field
          return;
        }
      }

      // VINEY: an answer overtaken by a newer cart paints nothing; a reconciliation read follows
      if (vnCart.stale(vnRead)) {
        vnCart.reconcile();
        return;
      }
      vnCart.seen(vnRead);
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
    } catch (error) {
      // VINEY (live bug 4): a network failure keeps the typed code and frees the button. TODO (T680): a
      // line for it; no approved string says "try again" for the code field yet.
    } finally {
      this.#activeFetch = null;
      applyButton?.removeAttribute('data-vn-wait');
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const vnRead = vnCart.next();
      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();
      Object.defineProperty(data, '__vnRead', { value: vnRead });

      if (vnCart.stale(vnRead)) {
        vnCart.reconcile();
        return;
      }
      vnCart.seen(vnRead);
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
